/**
 * Today.tsx · 09_APP · OS-006
 *
 * The opening page, rebuilt 2026-10-15 to the design Emily approved that day
 * (00_BRAIN/PROPOSALS/TODAY_PAGE_v2_2026-10-15.html, capability C35):
 * live sky banner with the quote of the day, the ask box, "Your day" (hours, water, feeling),
 * Start here, and what is waiting on her.
 *
 * 2026-10-20 (C45, D-0074): the ask box is now the Ask Claude bar shared by every page. It SAVES what she
 * says or types, with the page and person, and Claude works that list each session. It says exactly that,
 * because a control that looks live and quietly does nothing is not honest.
 * 2026-10-20 (C44, D-0073): "Next steps due" lists profile to-dos due today or earlier.
 * 2026-10-20 (C51, D-0086): the "how many women were in the room?" ask sits right under the Ask bar, and only
 * appears after an outreach on her calendar has ended. "Coming up" shows her next 7 days after Next steps.
 */

import { useEffect, useState } from "react";
import { os, OsError, type AttentionPayload } from "../lib/os";
import { Attention } from "./Attention";
import { SkyBand } from "./SkyBand";
import { StartHere } from "./StartHere";
import { YourDay } from "./YourDay";
import { ScanCard } from "./ScanCard";
import { AskClaude } from "./AskClaude";
import { ImpactStrip } from "./ImpactStrip";
import { ComingUp, NetworkingAsk, OutreachAsk } from "./ComingUp";
import type { DueStep } from "../lib/os";
import "../today.css";

const SUGGESTIONS = [
  "What needs me today?",
  "Who have I not talked to lately?",
  "Who gave this year?",
  "Draft this month's newsletter",
];

export function Today() {
  const [attention, setAttention] = useState<AttentionPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    os.attention()
      .then((a) => live && setAttention(a))
      .catch((e) => live && setError(e instanceof OsError ? e.message : String(e)));
    return () => { live = false; };
  }, []);

  function say(m: string) {
    setToast(m);
    window.setTimeout(() => setToast(null), 3200);
  }

  return (
    <main className="today">
      <SkyBand />

      {/* C45: the same Ask Claude bar as every other page, in the spot her 2026-10-15 design gave it. */}
      <div className="ask"><AskClaude suggestions={SUGGESTIONS} /></div>

      {/* D-0091, her words 2026-10-21: "I want the order to be the met and the card scan... And then like today, my
          calendar there." Met someone first, her calendar second, everything else after. Do not reorder. */}
      <ScanCard />

      <ComingUp />

      <OutreachAsk />

      <NetworkingAsk />

      <NextSteps />

      <YourDay />

      <StartHere say={say} />

      <h2 className="sec">Waiting on you <span>from the live database</span></h2>
      {error && <div className="refusebox">The database refused, or could not be reached: {error}</div>}
      {attention && <Attention data={attention} />}

      {/* C48 impact strip. D-0083, her words: "I want that at the bottom of the page. I liked the whole, did you
          meet somebody, at the top of the page. I liked exactly how it looked on my phone." So it lives last. */}
      <ImpactStrip />

      <footer className="foot">Connect 2 Change · Relationship OS</footer>
      <div className={toast ? "toast on" : "toast"} role="status">{toast}</div>
    </main>
  );
}

/** C44 section 3: "Shows on Today when due." Empty means nothing is due, so it says nothing. */
function NextSteps() {
  const [steps, setSteps] = useState<DueStep[]>([]);
  useEffect(() => { os.nextStepsDue().then(setSteps).catch(() => { /* Today still loads */ }); }, []);
  if (steps.length === 0) return null;
  return (
    <section className="nextdue">
      <h2 className="sec">Next steps due</h2>
      <ul>
        {steps.map((s) => (
          <li key={s.id}>{(() => {
            const inner = <><strong>{s.title}</strong><span>{s.name} · due {new Date(s.due_on + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span></>;
            return s.person_id ? <a href={`#people/${s.person_id}`}>{inner}</a> : <a className="nodue-link">{inner}</a>;
          })()}</li>
        ))}
      </ul>
    </section>
  );
}
